import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Filter, RefreshCw, WifiOff, AlertCircle, ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { leadQueriesApi } from '@/lib/api';
import type { LeadQuery, LeadQueryStatus } from '@/types/lead-query';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

const STATUS_STYLES: Record<LeadQueryStatus, string> = {
  new: 'bg-sky-100 text-sky-900 border-sky-200',
  open: 'bg-green-100 text-green-900 border-green-200',
  closed: 'bg-muted text-muted-foreground border-border',
};

interface QueryListProps {
  onSelect: (queryId: string) => void;
}

export const QueryList: React.FC<QueryListProps> = ({ onSelect }) => {
  const [queries, setQueries] = useState<LeadQuery[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | LeadQueryStatus>('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const load = useCallback(
    async (showRefresh = false) => {
      try {
        setError(null);
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        const res = await leadQueriesApi.list({
          page,
          limit,
          sortBy: 'updatedAt',
          sortOrder: 'desc',
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });

        if (res.statusCode !== 200) {
          throw new Error((res as { message?: string }).message || 'Failed to load queries');
        }
        setQueries((res.data.queries || []) as LeadQuery[]);
        setTotal(res.data.pagination?.total ?? 0);
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    if (isOnline) void load();
  }, [load, isOnline]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const formatShort = (iso?: string | null) => {
    if (!iso) return '—';
    return formatAdminBusinessEt(iso);
  };

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to load queries</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => void load()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!isOnline && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded text-sm flex items-center gap-2">
          <WifiOff className="h-4 w-4 shrink-0" />
          You&apos;re offline.
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="h-5 w-5" />
              Inbox
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search email, name, phone, query ID…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as 'all' | LeadQueryStatus);
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Updated</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {queries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                      <Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      No queries match your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  queries.map((q) => (
                    <TableRow key={q.queryId} className="cursor-pointer hover:bg-muted/50" onClick={() => onSelect(q.queryId)}>
                      <TableCell className="font-mono text-xs">{q.queryId}</TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{q.fullName?.trim() || "Website visitor"}</div>
                        <div className="text-xs text-muted-foreground break-all">
                          {q.email?.trim() || "Email not shared yet"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`capitalize ${STATUS_STYLES[q.status] || ''}`}>
                          {q.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                        {formatShort(q.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); onSelect(q.queryId); }}>
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
